import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import {
    Button,
    Grid,
    Box,
    CircularProgress,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Typography,
    Stack,
    Tooltip,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { isValid } from 'date-fns';
import { toDate, isDisplayableDate } from '../../../utils/dateHelpers';

import { getLastLipidAssessment, getLipidAssessmentById, gotoNextMenu, gotoPreviousMenu, saveLipidAssessment } from '../../../store/assessment.slice';
import { RootState } from '../../../reducers';
import { copyObject } from '../../../utils/common';
import { getActiveProviders } from '../../../store/provider.slice';
import { ProviderPreview } from '../../../Models/CarePlans/SchedulePlan.model';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_HGBA1C_Lipids_Urinalysis_Diabete_Foot.pdf';

const classes = {
    formControl: {
        marginBottom: '25px',
        padding: '0!important',
    },
};

const LipidAssessmentComponent: React.FC<{ memberId: string }> = ({ memberId }) => {
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { lipidAssessment, isFetching, isSaving } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const { isLoading } = useSelector((state: RootState) => state.shared);
    const { npProviders } = useSelector((state: RootState) => state.provider);

    useEffect(() => {
        if (Boolean(engagementDraft.LipidAssessmentId)) {
            dispatch(getLipidAssessmentById(engagementDraft.LipidAssessmentId));
        } else {
            dispatch(getLastLipidAssessment(memberId));
        }
        dispatch(getActiveProviders(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleOnChangeInput = (value: any, propsName: string, saveLocal = false) => {
        let data = copyObject(lipidAssessment);
        data[propsName] = value;
        const aiFlagsByField: Record<string, string[]> = {
            TestDate: ['IsTestDateAi'],
            LotNumber: ['IsLotNumberAi'],
            ExpirationDate: ['IsExpirationDateAi'],
            QcChipInformation: ['IsQcChipInformationAi'],
            Collector: ['IsCollectorAi'],
            TotalCholestrol: ['IsTotalCholestrolAi', 'IsTotalCholestrolOutOfRangeAi'],
            HDLCholestrol: ['IsHDLCholestrolAi', 'IsHDLCholestrolOutOfRangeAi'],
            LDLCholestrol: ['IsLDLCholestrolAi', 'IsLDLCholestrolOutOfRangeAi'],
            Triglycerides: ['IsTriglyceridesAi', 'IsTriglyceridesOutOfRangeAi'],
            eGlu: ['IseGluAi', 'IseGluOutOfRangeAi'],
        };
        const flagsToClear = aiFlagsByField[propsName];
        if (flagsToClear) {
            for (const flagName of flagsToClear) {
                if (Object.prototype.hasOwnProperty.call(data, flagName)) {
                    data[flagName] = false;
                }
            }
        }
        dispatch(saveLipidAssessment({ ...data, MemberId: memberId }, saveLocal));
    };

    const handleOnClickSave = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = (isWithoutComplete = false) => {
        if (isWithoutComplete) {
            dispatch(saveLipidAssessment({ ...lipidAssessment, Status: StatusEnum.Completed }, false, true));
        } else {
            dispatch(gotoNextMenu());
        }
    };

    const checkTotalCholestrolOutofRange = () => {
        return Boolean(lipidAssessment.TotalCholestrol) && (Number(lipidAssessment.TotalCholestrol) < 100 || Number(lipidAssessment.TotalCholestrol) > 400);
    };

    const checkHDLCholestrolOutofRange = () => {
        return Boolean(lipidAssessment.HDLCholestrol) && (Number(lipidAssessment.HDLCholestrol) < 20 || Number(lipidAssessment.HDLCholestrol) > 120);
    };

    const checkLDLCholestrolOutofRange = () => {
        return Boolean(lipidAssessment.LDLCholestrol) && (Number(lipidAssessment.LDLCholestrol) < 30 || Number(lipidAssessment.LDLCholestrol) > 300);
    };

    const checkTriglyceridesOutofRange = () => {
        return Boolean(lipidAssessment.Triglycerides) && (Number(lipidAssessment.Triglycerides) < 50 || Number(lipidAssessment.Triglycerides) > 500);
    };

    const checkEGluOutofRange = () => {
        return Boolean(lipidAssessment.eGlu) && (Number(lipidAssessment.eGlu) < 40 || Number(lipidAssessment.eGlu) > 600);
    };

    return (
        <Grid container direction="column" spacing={2} className="content-panel">
            <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 2 }}>
                <Typography variant="h6">Lipid Assessment</Typography>
                <Tooltip title="Lipid Assessment Help Document">
                    <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                </Tooltip>
            </Stack>
            {isFetching || isLoading ? (
                <Box display="flex" justifyContent="center" className="loader-container">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Grid size={{ sm: 12, md: 12, lg: 12 }}>{/* <h6>Last Completed :-</h6> */}</Grid>
                    <Grid className="content-form">
                        <Grid container spacing={4}>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <Typography variant="h5" sx={{ mb: 4 }}>
                                    Member Information
                                </Typography>
                                <Box sx={classes.formControl}>
                                    <TextField label="Member Name" variant="outlined" size="small" fullWidth disabled value={lipidAssessment.MemberName} />
                                </Box>
                                <Box sx={classes.formControl}>
                                    <DesktopDatePicker
                                        label="DOB"
                                        disabled
                                        value={Boolean(lipidAssessment.DOB) ? new Date(lipidAssessment.DOB) : null}
                                        format="MM/dd/yyyy"
                                        onChange={(date) => {}}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Box>
                                <Box sx={classes.formControl}>
                                    <TextField label="Age" variant="outlined" size="small" fullWidth disabled value={String(lipidAssessment.Age)} />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                                <Typography variant="h5" sx={{ mb: 4 }}>
                                    Test Information
                                </Typography>
                                <Grid container spacing={2} rowGap={1.2}>
                                    <Grid size={12}>
                                        <DesktopDatePicker
                                            label={AssessmentQuestions.LipidAssessment_DateOfTest}
                                            value={isDisplayableDate(lipidAssessment.TestDate) ? toDate(lipidAssessment.TestDate) : null}
                                            format="MM/dd/yyyy"
                                            onChange={(date) => {
                                                if (
                                                    (() => {
                                                        const d = toDate(date);
                                                        return d && isValid(d);
                                                    })() ||
                                                    !Boolean(date)
                                                ) {
                                                    handleOnChangeInput(date.toISOString(), 'TestDate');
                                                }
                                            }}
                                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="provider-type-select">Providers Name</InputLabel>
                                            <Select
                                                labelId="provider-type-select"
                                                id="provider-type-select"
                                                value={Boolean(lipidAssessment.Provider?.Id) ? String(lipidAssessment.Provider.Id) : ''}
                                                label="Providers Name"
                                                name="Provider"
                                                onChange={(e) => {
                                                    const selectedProvider = npProviders.find((p) => p.Id === e.target.value);
                                                    handleOnChangeInput(
                                                        {
                                                            Id: selectedProvider.Id,
                                                            Name: selectedProvider.FullName,
                                                            Role: selectedProvider.Role?.RoleName,
                                                        } as ProviderPreview,
                                                        e.target.name
                                                    );
                                                }}
                                            >
                                                {npProviders.map((provider) => (
                                                    <MenuItem key={provider.Id} value={provider.Id}>
                                                        {provider.FullName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            disabled
                                            label={AssessmentQuestions.LipidAssessment_CollectorsName}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={lipidAssessment.Collector?.Name || ''}
                                            name="CollectorName"
                                            InputProps={{
                                                endAdornment: lipidAssessment.IsCollectorAi && isAIIcon(lipidAssessment.Collector),
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label={AssessmentQuestions.LipidAssessment_LotNumber}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={lipidAssessment.LotNumber}
                                            name="LotNumber"
                                            onChange={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                            InputProps={{
                                                endAdornment: lipidAssessment.IsLotNumberAi && isAIIcon(lipidAssessment.LotNumber),
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <DesktopDatePicker
                                            label={
                                                <Typography>
                                                    {AssessmentQuestions.LipidAssessment_ExpirationDate} {lipidAssessment.IsExpirationDateAi && isAIIcon(lipidAssessment.ExpirationDate)}
                                                </Typography>
                                            }
                                            value={isDisplayableDate(lipidAssessment.ExpirationDate) ? toDate(lipidAssessment.ExpirationDate) : null}
                                            format="MM/dd/yyyy"
                                            onChange={(date) => {
                                                if (
                                                    (() => {
                                                        const d = toDate(date);
                                                        return d && isValid(d);
                                                    })() ||
                                                    !Boolean(date)
                                                ) {
                                                    handleOnChangeInput(date.toISOString(), 'ExpirationDate');
                                                }
                                            }}
                                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label={AssessmentQuestions.LipidAssessment_QCChipInformation}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={lipidAssessment.QcChipInformation}
                                            name="QcChipInformation"
                                            onChange={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                            InputProps={{
                                                endAdornment: lipidAssessment.IsQcChipInformationAi && isAIIcon(lipidAssessment.QcChipInformation),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <Typography variant="h5" sx={{ mb: 4 }}>
                                    Result Of Analysis
                                </Typography>

                                <Grid container spacing={2} rowGap={1.2}>
                                    <Grid size={12}>
                                        <Grid container spacing={2}>
                                            <Grid size={6}>
                                                <TextField
                                                    label={AssessmentQuestions.LipidAssessment_TotalCholesterol}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    type="number"
                                                    name="TotalCholestrol"
                                                    value={lipidAssessment.TotalCholestrol}
                                                    onChange={(e) => handleOnChangeInput(e.target.value, e.target.name, true)}
                                                    onBlur={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                                    error={lipidAssessment.IsTotalCholestrolOutOfRange || checkTotalCholestrolOutofRange()}
                                                    InputProps={{
                                                        endAdornment: lipidAssessment.IsTotalCholestrolAi && isAIIcon(lipidAssessment.TotalCholestrol),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormControlLabel
                                                    sx={{ mr: 0, mb: 0 }}
                                                    control={
                                                        <Checkbox
                                                            checked={lipidAssessment.IsTotalCholestrolOutOfRange}
                                                            name="IsTotalCholestrolOutOfRange"
                                                            onChange={(e) => {
                                                                handleOnChangeInput(e.target.checked, e.target.name);
                                                                handleOnChangeInput(false, 'IsTotalCholestrolAi');
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography>
                                                            {AssessmentQuestions.LipidAssessment_IsTotalCholesterolOutOfRange}{' '}
                                                            {lipidAssessment.IsTotalCholestrolAi && isAIIcon(lipidAssessment.TotalCholestrol)}
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12}>
                                        <Grid container spacing={2} rowGap={0}>
                                            <Grid size={6}>
                                                <TextField
                                                    label={AssessmentQuestions.LipidAssessment_HDLCholesterol}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    type="number"
                                                    name="HDLCholestrol"
                                                    value={lipidAssessment.HDLCholestrol}
                                                    onChange={(e) => handleOnChangeInput(e.target.value, e.target.name, true)}
                                                    onBlur={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                                    error={lipidAssessment.IsHDLCholestrolOutOfRange || checkHDLCholestrolOutofRange()}
                                                    InputProps={{
                                                        endAdornment: lipidAssessment.IsHDLCholestrolOutOfRangeAi && isAIIcon(lipidAssessment.HDLCholestrol),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormControlLabel
                                                    sx={{ mr: 0, mb: 0 }}
                                                    control={
                                                        <Checkbox
                                                            checked={lipidAssessment.IsHDLCholestrolOutOfRange}
                                                            name="IsHDLCholestrolOutOfRange"
                                                            onChange={(e) => {
                                                                handleOnChangeInput(e.target.checked, e.target.name);
                                                                handleOnChangeInput(false, 'IsHDLCholestrolOutOfRangeAi');
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography>
                                                            {AssessmentQuestions.LipidAssessment_IsHDLCholesterolOutOfRange}{' '}
                                                            {lipidAssessment.IsHDLCholestrolOutOfRangeAi && isAIIcon(lipidAssessment.HDLCholestrol)}
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12}>
                                        <Grid container spacing={2} rowGap={0}>
                                            <Grid size={6}>
                                                <TextField
                                                    label={AssessmentQuestions.LipidAssessment_LDLCholesterol}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    type="number"
                                                    name="LDLCholestrol"
                                                    value={lipidAssessment.LDLCholestrol}
                                                    onChange={(e) => handleOnChangeInput(e.target.value, e.target.name, true)}
                                                    onBlur={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                                    error={lipidAssessment.IsLDLCholestrolOutOfRange || checkLDLCholestrolOutofRange()}
                                                    InputProps={{
                                                        endAdornment: lipidAssessment.IsLDLCholestrolOutOfRangeAi && isAIIcon(lipidAssessment.LDLCholestrol),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormControlLabel
                                                    sx={{ mr: 0, mb: 0 }}
                                                    control={
                                                        <Checkbox
                                                            checked={lipidAssessment.IsLDLCholestrolOutOfRange}
                                                            name="IsLDLCholestrolOutOfRange"
                                                            onChange={(e) => {
                                                                handleOnChangeInput(e.target.checked, e.target.name);
                                                                handleOnChangeInput(false, 'IsLDLCholestrolOutOfRangeAi');
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography>
                                                            {AssessmentQuestions.LipidAssessment_IsLDLCholesterolOutOfRange}{' '}
                                                            {lipidAssessment.IsLDLCholestrolOutOfRangeAi && isAIIcon(lipidAssessment.LDLCholestrol)}
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12}>
                                        <Grid container spacing={2}>
                                            <Grid size={6}>
                                                <TextField
                                                    label={AssessmentQuestions.LipidAssessment_Triglycerides}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    type="number"
                                                    name="Triglycerides"
                                                    value={lipidAssessment.Triglycerides}
                                                    onChange={(e) => handleOnChangeInput(e.target.value, e.target.name, true)}
                                                    onBlur={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                                    error={lipidAssessment.IsTriglyceridesOutOfRange || checkTriglyceridesOutofRange()}
                                                    InputProps={{
                                                        endAdornment: lipidAssessment.IsTriglyceridesOutOfRangeAi && isAIIcon(lipidAssessment.Triglycerides),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormControlLabel
                                                    sx={{ mr: 0, mb: 0 }}
                                                    control={
                                                        <Checkbox
                                                            checked={lipidAssessment.IsTriglyceridesOutOfRange}
                                                            name="IsTriglyceridesOutOfRange"
                                                            onChange={(e) => {
                                                                handleOnChangeInput(e.target.checked, e.target.name);
                                                                handleOnChangeInput(false, 'IsTriglyceridesOutOfRangeAi');
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography>
                                                            {AssessmentQuestions.LipidAssessment_IsTriglyceridesOutOfRange}{' '}
                                                            {lipidAssessment.IsTriglyceridesOutOfRangeAi && isAIIcon(lipidAssessment.Triglycerides)}
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12}>
                                        <Grid container spacing={2}>
                                            <Grid size={6}>
                                                <TextField
                                                    label={AssessmentQuestions.LipidAssessment_eGLUCholesterol}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    type="number"
                                                    name="eGlu"
                                                    value={lipidAssessment.eGlu}
                                                    onChange={(e) => handleOnChangeInput(e.target.value, e.target.name, true)}
                                                    onBlur={(e) => handleOnChangeInput(e.target.value, e.target.name)}
                                                    error={lipidAssessment.IseGluOutOfRange || checkEGluOutofRange()}
                                                    InputProps={{
                                                        endAdornment: lipidAssessment.IseGluOutOfRangeAi && isAIIcon(lipidAssessment.eGlu),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormControlLabel
                                                    sx={{ mr: 0, mb: 0 }}
                                                    control={
                                                        <Checkbox
                                                            checked={lipidAssessment.IseGluOutOfRange}
                                                            name="IseGluOutOfRange"
                                                            onChange={(e) => {
                                                                handleOnChangeInput(e.target.checked, e.target.name);
                                                                handleOnChangeInput(false, 'IseGluOutOfRangeAi');
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography>
                                                            {AssessmentQuestions.LipidAssessment_IseGLUCholesterolOutOfRange}{' '}
                                                            {lipidAssessment.IseGluOutOfRangeAi && isAIIcon(lipidAssessment.eGlu)}
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid justifyContent="flex-end" className="content-footer">
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 3 }}
                            onClick={() => dispatch(gotoPreviousMenu())}
                            startIcon={<ArrowBackIosIcon />}
                        >
                            Previous
                        </Button>
                        <Button type="submit" variant="contained" onClick={handleOnClickSave} endIcon={<ArrowForwardIosIcon />}>
                            Next
                        </Button>
                    </Grid>
                </>
            )}
            {Boolean(engagementDraft.LipidAssessmentId) ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="Lipid Assessment"
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={() => handleOnConfirmToNext(true)}
                    onSkip={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isSaving}
                    message="I confirm and acknowledge that Lipid assessment is completed."
                />
            ) : (
                <AlertDialog
                    open={showSaveAlert}
                    title="Lipid Assessment"
                    cancelText="No"
                    okText="Yes, Proceed"
                    onConfirm={handleOnConfirmToNext}
                    onClose={() => setShowSaveAlert(false)}
                    message="Are you sure to proceed without completing Lipid assessment?"
                />
            )}
        </Grid>
    );
};

export default LipidAssessmentComponent;
